import { Box, styled, useMediaQuery } from "@mui/material";
import { useMemo } from "react";
import { formatMoney } from "../../../../utils/StringUtils";
import { MOBILE_SCREEN_SIZE } from "../../../AppNavigation/constants";
import { StyledLink } from "../../../AppNavigation/StyledLink";
import { useLinkPrefix } from "../../../../utils/hooks/useLinkPrefix";
import { RewardsForBrandClub } from "../../../../api/graphQl/authenticated/Reward";

const Pill = styled(StyledLink)({
  borderRadius: 25,
  fontSize: 14,
  fontWeight: 600,
  backgroundColor: "#f6f8fa",
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 16px",
  textDecoration: "none",
  whiteSpace: "nowrap",

  transition: "transform 0.3s",
  "&:active": {
    "&:not([disabled])": {
      transform: "scale(0.95)",
    },
  },
});

interface RewardPill {
  title: string;
  value?: number | string;
  link: string;
}

const BrandstoreRewardPillSection = ({
  rewards,
}: {
  rewards: RewardsForBrandClub | undefined;
}) => {
  const prefix = useLinkPrefix();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);
  const linkPrefix = prefix.linkPrefix + "/branddashboard";
  const lifetimeEarnings = rewards?.lifetimeEarnings ?? 0;
  const expiringRewardAmount = rewards?.expiringRewardAmount;
  const lifetimePayouts = rewards?.lifetimePayouts ?? 0;
  const lifetimeAdjustments = rewards?.lifetimeAdjustments ?? 0;
  const lifetimeActivityCount = rewards?.lifetimeActivityCount;
  const calculatedRewardBalance =
    lifetimeEarnings - lifetimePayouts - lifetimeAdjustments;

  const rewardPills = useMemo(() => {
    const totalRewards: RewardPill = {
      title: "Total rewards",
      value: lifetimeEarnings ? formatMoney(lifetimeEarnings) : undefined,
      link: linkPrefix + "/rewards/balance",
    };
    const rewardBalance: RewardPill = {
      title: "Reward balance",
      value: calculatedRewardBalance
        ? formatMoney(calculatedRewardBalance)
        : undefined,
      link: linkPrefix + "/rewards/balance",
    };
    const activity: RewardPill = {
      title: "Activity",
      link: linkPrefix + "/rewards/activity",
      value: lifetimeActivityCount ? lifetimeActivityCount : undefined,
    };
    const purchases: RewardPill = {
      title: "Purchases",
      link: linkPrefix + "/purchase",
    };
    const expiringReward: RewardPill = {
      title: "Expiring rewards",
      value: expiringRewardAmount
        ? formatMoney(expiringRewardAmount)
        : undefined,
      link: linkPrefix + "/rewards/expiring",
    };
    // use different order for mobile
    const pills = isMobile
      ? [totalRewards, expiringReward, rewardBalance, activity, purchases]
      : [totalRewards, rewardBalance, activity, purchases, expiringReward];
    return pills;
  }, [
    lifetimeEarnings,
    linkPrefix,
    expiringRewardAmount,
    isMobile,
    calculatedRewardBalance,
    lifetimeActivityCount,
  ]);

  return (
    <Box
      marginBottom={2.5}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      gap={1.25}
    >
      {rewardPills.map((reward) => (
        <Pill
          to={reward.link}
          key={reward.title}
          sx={{
            padding: !!reward.value ? "8px 12px" : undefined,
          }}
        >
          <Box component="span">{reward.title}</Box>
          {!!reward.value && (
            <Box
              component="span"
              sx={(theme) => ({
                borderRadius: "25px",
                padding: "3px 8.5px",
                marginLeft: "11px",
                fontSize: 12,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
              })}
            >
              {reward.value}
            </Box>
          )}
        </Pill>
      ))}
    </Box>
  );
};

export default BrandstoreRewardPillSection;
